<script lang="ts" setup>
import {
  Assignment
} from "classes/models/assignments/assignment.model";
import {
  Classroom
} from "classes/models/classrooms/classroom.model";
import { Student } from "classes/models/students/student.model";
import type { Checklist } from "types/Checklist";
import { SubmissionJob } from "~/classes/models/submission-job/submission-job.model";

const props = defineProps({
  job: {
    type: Object as PropType<SubmissionJob>,
    required: true,
  },
});

const emit = defineEmits(["close", "upload"]);

const selectedChecklist = ref<Checklist | undefined>(props.job.checklist);
const selectedClassroom = ref<Classroom | undefined>(props.job.classroom);
const selectedAssignment = ref<Assignment | undefined>(props.job.assignment);

const selectedStudent = ref<Student | undefined>(props.job.student);

const onChangedSelectedChecklist = (checklistId: string) => {
  selectedChecklist.value = props.job.checklists?.find(
    (checklist) => checklist.id == checklistId
  );
};

const onChangedSelectedStudent = (studentId: string) => {
  selectedStudent.value = props.job.students?.find(
    (student) => student.id == studentId
  );
};

const isUploading = ref(false);
const errorMessage = ref("");

const onUpload = async () => {
  if (isUploading.value) return;
  errorMessage.value = "";
  if (props.job.checklists && selectedChecklist.value == undefined) {
    useBaseToast("Please select a checklist.", "error");
    return;
  }

  if (showStudentSelect.value == true && selectedStudent.value == undefined) {
    useBaseToast("Please select a student.", "error");
    return;
  }

  isUploading.value = true;

  props.job.checklist = selectedChecklist.value;
  props.job.student = selectedStudent.value;
  props.job.classroom = selectedClassroom.value;
  props.job.assignment = selectedAssignment.value;

  try {
    await props.job.uploadFiles();
    props.job.sheetController.close();
  } catch (error) {
    console.error("Submission upload failed", error);
    errorMessage.value = "Upload failed. Your files are still selected. Check your connection and try again.";
  } finally {
    isUploading.value = false;
  }
};

const showClassroomSelect = computed(() => {
  return props.job.classrooms != undefined && props.job.classrooms.length > 0;
});

const showAssignmentSelect = computed(() => {
  return (
    props.job.assignments != undefined &&
    props.job.assignments.length > 0 &&
    selectedClassroom.value != undefined
  );
});

const showStudentSelect = computed(() => {
  return (
    props.job.students != undefined &&
    props.job.students.length > 0 &&
    (props.job.classrooms == undefined || selectedAssignment.value != undefined)
  );
});

const onChangedSelectedClassroom = (classroomId: string) => {
  selectedClassroom.value = props.job.classrooms?.find(
    (classroom) => classroom.id == classroomId
  );
};

const onChangedSelectedAssignment = (assignmentId: string) => {
  selectedAssignment.value = props.job.assignments?.find(
    (assignment) => assignment.id == assignmentId
  );

  if (
    selectedAssignment.value != undefined &&
    selectedAssignment.value.checklistId != undefined
  ) {
    onChangedSelectedChecklist(selectedAssignment.value.checklistId);
  }
};

const assignmentsToShow = computed(() => {
  if (selectedClassroom.value == undefined) return [];

  const assignmentsList = props.job.assignments?.filter(
    (assignment) => assignment.classroomId == selectedClassroom.value?.id
  );

  return useSortAssignments(assignmentsList ?? []);
});

const studentsToShow = computed(() => {
  if (props.job.classrooms == undefined) return props.job.students;

  if (
    selectedAssignment.value == undefined &&
    selectedClassroom.value?.id != undefined
  )
    return [];

  return props.job.students?.filter((student) =>
    student.classroomIds.includes(selectedClassroom.value!.id!)
  );
});

const sortedChecklists = computed(() => {
  const c = useSortChecklists(props.job.checklists ?? []);
  return c;
});
</script>

<template>
  <div class="flex flex-col justify-between w-full max-w-[600px] overflow-auto p-4">
    <div class="flex flex-col">
      <BaseFormComponent v-if="job.checklists">
        <template #label> Choose a Checklist </template>
        <ChecklistsSelect
          :model-value="selectedChecklist?.id"
          :checklists="sortedChecklists"
          @update:model-value="onChangedSelectedChecklist"
        />
      
      </BaseFormComponent>
      <div
        v-if="showClassroomSelect"
        class="h-[1px] bg-medium-default rounded mb-6 mt-4"
      >
        &nbsp;
      </div>
      <BaseFormComponent v-if="showClassroomSelect">
        <template #label> Choose a Classroom (optional) </template>
        <BaseSelectInput
          :options="props.job.classrooms"
          value-prop="id"
          label="label"
          :searchable="true"
          :can-clear="true"
          :model-value="selectedClassroom?.id"
          @update:model-value="onChangedSelectedClassroom"
        />
      </BaseFormComponent>

      <BaseFormComponent v-if="showAssignmentSelect">
        <template #label> Choose an Assignment </template>
        <BaseSelectInput
          :options="assignmentsToShow"
          value-prop="id"
          label="name"
          :searchable="true"
          :can-clear="true"
          :model-value="selectedAssignment?.id"
          @update:model-value="onChangedSelectedAssignment"
        />
      </BaseFormComponent>

      <BaseFormComponent v-if="showStudentSelect">
        <template #label> Choose a Student </template>
        <BaseSelectInput
          :options="studentsToShow"
          value-prop="id"
          label="name"
          :searchable="true"
          :can-clear="true"
          :model-value="selectedStudent?.id"
          @update:model-value="onChangedSelectedStudent"
        />
      </BaseFormComponent>
    </div>
    <p v-if="errorMessage" role="alert" class="mb-3 text-danger-default">{{ errorMessage }}</p>
    <div class="flex flex-row items-center justify-start gap-2">
      <BaseButton
        @click="onUpload"
        :disabled="isUploading"
        :show-spinner="isUploading"
      >
        <div class="flex flex-row items-center gap-1">
          <BaseIconUpload />
          Upload
        </div>
      </BaseButton>
      <BaseTextButton
        color="danger"
        class="mr-2"
        @click="job.sheetController.close()"
      >
        Cancel
      </BaseTextButton>
    </div>
  </div>
</template>

<style scoped></style>
